/**
 * LayoutContextProvider - Провайдер контексту для Layout
 *
 * Адаптована версія DefaultLayoutContextProvider
 * для роботи з UIManager замість DefaultLayoutStore
 */

import React, { createContext, ReactNode, useContext } from 'react'
import { LayoutTemplateConfig } from '../../Commands/Loaders/LayoutTemplateLoader'
import type { IUIManagerConfig } from '../../Contracts/UI/UIManager'
import { UIManager } from '../../Managers/UI/UIManager'
import { type KernelLike } from '../../Services/KernelRegistry'

export interface LayoutContextConfig extends Omit<IUIManagerConfig, 'template'> {
    // Layout specific props
    template?: string
    kernel?: KernelLike
    debug?: {
        id?: string
        name?: string
        type?: 'page' | 'layout' | 'component' | 'container' | 'template'
        path?: string
        parentId?: string
        enabled?: boolean
    }
}

export const LayoutContext = createContext<UIManager | undefined>(undefined)

export const createLayoutContext = (config: LayoutContextConfig = {}): UIManager => {
    const { template, kernel, debug, children, accessFn, templateError403, ...uiManagerConfig } = config

    // Отримуємо компонент шаблону з автоматично згенерованої конфігурації
    const TemplateComponent = template ? LayoutTemplateConfig[template] : null

    if (!TemplateComponent) {
        console.error(
            `❌ Шаблон "${template}" не знайдено в LayoutTemplateConfig. Доступні шаблони:`,
            Object.keys(LayoutTemplateConfig)
        )

        // Створюємо fallback компонент для відображення помилки
        const ErrorComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
            <div
                style={{
                    padding: '20px',
                    color: 'red',
                    border: '1px solid red',
                    borderRadius: '4px',
                    margin: '20px',
                }}
            >
                <h3>⚠️ Помилка шаблону</h3>
                <p>Шаблон "{template}" не знайдено в конфігурації.</p>
                <p>Доступні шаблони: {Object.keys(LayoutTemplateConfig).join(', ')}</p>
                {children}
            </div>
        )

        return new UIManager({
            ...uiManagerConfig,
            contextKey: 'layoutContext',
            template: ErrorComponent,
            children,
            debug,
        })
    }

    // Витягуємо accessFn та templateError403 з модуля шаблону
    // Пріоритет: 1) з config роута, 2) з модуля шаблону, 3) undefined
    const templateModule = TemplateComponent as any
    const finalAccessFn = accessFn || templateModule?.accessFn
    const finalTemplateError403 = templateError403 || templateModule?.templateError403

    return new UIManager({
        ...uiManagerConfig,
        contextKey: 'layoutContext',
        // Встановлюємо template як React компонент
        template: TemplateComponent,
        // Передаємо children напряму, не через props
        children,
        // Використовуємо accessFn та templateError403 з шаблону або конфігурації роута
        accessFn: finalAccessFn,
        templateError403: finalTemplateError403,
        states: {
            template,
            templateName: template,
            ...uiManagerConfig.states,
        },
        settings: {
            template,
            templateName: template,
            templateComponent:
                (TemplateComponent as any)?.displayName || (TemplateComponent as any)?.name || 'Anonymous',
            ...uiManagerConfig.settings,
        },
        debug: debug
            ? {
                  ...debug,
                  // Оновлюємо ім'я для включення імені шаблону
                  name: debug.name || `Template: ${template}`,
                  type: 'template', // Перевизначаємо тип на template
              }
            : undefined,
    })
}

export const useLayoutContext = (): UIManager|undefined => {
    const context = useContext(LayoutContext)
    return context
}

interface LayoutContextProviderProps {
    value: UIManager
    children: ReactNode
}

export const LayoutContextProvider: React.FC<LayoutContextProviderProps> = ({ value, children }) => (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
)
